/**
 * Direct API Client
 *
 * Makes requests directly to the backend API using NextAuth session tokens.
 * This eliminates the proxy hop for better performance.
 *
 * Use this client for client-side data fetching where:
 * - You need authenticated requests to the backend
 * - You want to avoid the /api/proxy round-trip
 * - The backend allows direct requests from the frontend domain
 *
 * For binary content (PDFs, file downloads), continue using the proxy.
 */

import { getDataConfig } from '@/lib/config/data-config';
import { logger } from '@/lib/utils/logger';
import { authGate, AuthGateError } from '@/lib/api/auth-gate';
import { getCachedSession } from '@/lib/api/session-cache';
import { normaliseApiErrorMessage } from '@/lib/api/error-message';
import { handleMembershipError } from '@/lib/utils/membership-error-handler';

// ============================================================================
// TYPES
// ============================================================================

type QueryParams = Record<string, unknown>;

export interface ApiErrorResponse {
  /** NestJS sends a single string for most errors, but a `string[]` for
   *  class-validator failures (one entry per failing constraint). */
  message?: string | string[];
  error?: string;
  statusCode?: number;
  /** Structured error code (e.g. `ASSET_BELONGS_TO_PAYOUT`,
   *  `LIABILITY_REFERENCED_BY_DISTRIBUTION`). */
  code?: string;
  /** Backend-provided extra context for structured error codes. */
  details?: Record<string, unknown>;
  /** Merge-field tags the matter is missing, returned alongside
   *  `DECLARATION_BLOCKED_ON_DATA` so the caller can list what to fix. */
  missingRequired?: string[];
  /** The offending matter id(s), returned alongside `LEAD_MATTER_DIVERGENCE`
   *  so the caller can name which matter to unbind an artefact from. */
  matterIds?: number[];
  /** When the in-flight run started, returned on the document-sync audit's 409
   *  so a dialog reopened mid-run can ATTACH to that run rather than starting a
   *  rival. Two overlapping runs would both read "no receipt" for one document
   *  and both upload it — a duplicate in the firm's PMS, which is the one
   *  outcome that feature cannot undo. ISO 8601. */
  runningSince?: string;
  /** When that run's lock expires, so a crashed run is recoverable from this
   *  instant on and cannot wedge the firm out permanently. ISO 8601. */
  lockExpiresAt?: string;
  /** The integration whose credential failed, returned alongside
   *  `INTEGRATION_REAUTH_REQUIRED` so the caller can name it. The wire message
   *  is deliberately provider-neutral — the brand name is the frontend's, from
   *  `getPmsProviderPresentation`. */
  provider?: string;
  /** Why the Outlook send identity couldn't send (`OUTLOOK_SEND_BLOCKED`), or
   *  why a shared mailbox failed its registration probe
   *  (`OUTLOOK_SHARED_MAILBOX_UNREACHABLE`). Pairs with `reasonMessage`. */
  reason?: string;
  /** The reason-specific sentence for `reason`, returned alongside
   *  `OUTLOOK_SEND_BLOCKED` and `OUTLOOK_SHARED_MAILBOX_UNREACHABLE` so the
   *  caller can show what to do about it rather than the generic wire message.
   *  The copy is the backend's — for the send block it is shared with the
   *  vale-ops staff portal (VALE-348) — so never rebuild it from `reason` here. */
  reasonMessage?: string;
  /** Per-recipient block detail returned alongside `OUTLOOK_SEND_BLOCKED`.
   *  Degenerates to one entry for a single send. */
  blocked?: {
    correspondenceId: number;
    recipient: string | null;
    reason: string;
  }[];
}

export interface DirectRequestOptions extends Omit<RequestInit, 'body'> {
  params?: QueryParams;
  timeout?: number;
}

export class DirectApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: ApiErrorResponse
  ) {
    super(message);
    this.name = 'DirectApiError';
  }

  static fromResponse(
    response: Response,
    errorData?: ApiErrorResponse
  ): DirectApiError {
    // `normaliseApiErrorMessage` owns the class-validator `string[]` handling —
    // coercing that array straight into `Error` would join it with bare commas.
    const message =
      normaliseApiErrorMessage(errorData?.message) ||
      errorData?.error ||
      `Request failed with status ${response.status}`;
    return new DirectApiError(message, response.status, errorData);
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Build query string from parameters object
 */
function buildQueryString(params?: QueryParams): string {
  if (!params) return '';

  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach((v) => searchParams.append(key, String(v)));
      } else {
        searchParams.append(key, String(value));
      }
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Get authentication headers from NextAuth session
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const session = await getCachedSession();

  if (!session?.accessToken) {
    throw new DirectApiError('Not authenticated', 401);
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${session.accessToken}`
  };

  if (session.tenantId) {
    headers['X-Tenant-Id'] = session.tenantId;
  }

  return headers;
}

// ============================================================================
// DIRECT API CLIENT
// ============================================================================

/**
 * Make a direct request to the backend API
 */
async function request<T>(
  method: string,
  endpoint: string,
  body?: unknown,
  options: DirectRequestOptions = {}
): Promise<T> {
  const config = getDataConfig();
  const baseUrl = config.apiBaseUrl;

  const requestId = Math.random().toString(36).substring(7);
  const startTime = Date.now();

  logger.debug(`[Direct ${requestId}] ${method} ${endpoint}`);

  // Build full URL
  const queryString = buildQueryString(options.params);
  const url = `${baseUrl}${endpoint}${queryString}`;

  // Get auth headers
  const authHeaders = await getAuthHeaders();

  // Setup timeout — merge any caller-provided signal (e.g. React Query's
  // queryFn signal) with our timeout signal so unmount cancellation actually
  // aborts the in-flight fetch. See LF-3059.
  const controller = new AbortController();
  const timeout = options.timeout || 30000;
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  const signal = options.signal
    ? AbortSignal.any([options.signal, controller.signal])
    : controller.signal;

  /**
   * `FormData` goes to the wire untouched, and **without** a `Content-Type`.
   *
   * Everything else here is JSON, so the header is set unconditionally and the
   * body stringified — which silently turns a `FormData` into the string `"{}"`.
   * That is why the shipped signed-Distribution-Advice upload hand-rolls its own
   * `fetch` with its own auth-header assembly, and it is the copy a second
   * multipart caller would otherwise make a third of. Handling it here keeps
   * every caller on one client (auth headers, AuthGate refresh/retry, the
   * timeout+abort merge, the error shape) instead.
   *
   * The header must be **omitted**, not set: only the browser can generate the
   * `multipart/form-data; boundary=…` value, and a hand-written one produces a
   * body the server cannot parse.
   */
  const isMultipart =
    typeof FormData !== 'undefined' && body instanceof FormData;

  const requestOptions: RequestInit = {
    method,
    headers: {
      ...(isMultipart ? {} : { 'Content-Type': 'application/json' }),
      ...authHeaders,
      ...options.headers
    },
    signal
  };

  if (body !== undefined) {
    requestOptions.body = isMultipart
      ? (body as FormData)
      : JSON.stringify(body);
  }

  try {
    // Route through AuthGate — handles 401 detection, token refresh,
    // request pausing, and retry transparently.
    // Auth headers are re-fetched on retry so the new token is used.
    const doFetch = async () => {
      const freshAuthHeaders = await getAuthHeaders();
      const opts: RequestInit = {
        ...requestOptions,
        headers: {
          ...requestOptions.headers,
          ...freshAuthHeaders
        }
      };
      return fetch(url, opts);
    };
    const response = await authGate.gate(doFetch);
    clearTimeout(timeoutId);

    const duration = Date.now() - startTime;
    logger.debug(
      `[Direct ${requestId}] Response: ${response.status} (${duration}ms)`
    );

    if (!response.ok) {
      let errorData: ApiErrorResponse;
      try {
        errorData = (await response.json()) as ApiErrorResponse;
      } catch {
        errorData = {
          message: `HTTP ${response.status}: ${response.statusText}`
        };
      }

      // Surface membership-status 403s (SUSPENDED / CANCELLED) as an
      // actionable toast before the error propagates. Unrecognised 403s
      // fall through to the caller's normal handling.
      handleMembershipError(
        response.status,
        errorData.code,
        Array.isArray(errorData.message)
          ? errorData.message.join('; ')
          : errorData.message
      );

      throw DirectApiError.fromResponse(response, errorData);
    }

    // Handle empty responses
    if (
      response.status === 204 ||
      response.headers.get('content-length') === '0'
    ) {
      return undefined as unknown as T;
    }

    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      return undefined as unknown as T;
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof DirectApiError) {
      throw error;
    }

    if (error instanceof AuthGateError) {
      throw new DirectApiError(error.message, 401);
    }

    if (error instanceof Error && error.name === 'AbortError') {
      throw new DirectApiError('Request timeout', 408);
    }

    throw new DirectApiError(
      error instanceof Error ? error.message : 'Network error',
      0
    );
  }
}

// ============================================================================
// PUBLIC API
// ============================================================================

export const directApiClient = {
  /**
   * GET request
   */
  get<T>(endpoint: string, options?: DirectRequestOptions): Promise<T> {
    return request<T>('GET', endpoint, undefined, options);
  },

  /**
   * POST request
   */
  post<T, TBody = unknown>(
    endpoint: string,
    body?: TBody,
    options?: DirectRequestOptions
  ): Promise<T> {
    return request<T>('POST', endpoint, body, options);
  },

  /**
   * PUT request
   */
  put<T, TBody = unknown>(
    endpoint: string,
    body?: TBody,
    options?: DirectRequestOptions
  ): Promise<T> {
    return request<T>('PUT', endpoint, body, options);
  },

  /**
   * PATCH request
   */
  patch<T, TBody = unknown>(
    endpoint: string,
    body?: TBody,
    options?: DirectRequestOptions
  ): Promise<T> {
    return request<T>('PATCH', endpoint, body, options);
  },

  /**
   * DELETE request
   *
   * A body argument is supported (used by the templates archive flow to pass
   * `replacementDefaultId`). RFC 7231 leaves DELETE bodies as
   * implementation-defined; the backend's archive endpoint reads the body
   * when present and ignores it otherwise.
   */
  delete<T = void, TBody = unknown>(
    endpoint: string,
    body?: TBody,
    options?: DirectRequestOptions
  ): Promise<T> {
    return request<T>('DELETE', endpoint, body, options);
  }
};

// ============================================================================
// REACT QUERY FETCHER
// ============================================================================

/**
 * Create a fetcher function for React Query that uses the direct client
 *
 * @example
 * const { data } = useQuery({
 *   queryKey: ['matters', matterId],
 *   queryFn: createDirectFetcher(`/matters/${matterId}`)
 * });
 */
export function createDirectFetcher<T>(
  endpoint: string,
  options?: DirectRequestOptions
): () => Promise<T> {
  return () => directApiClient.get<T>(endpoint, options);
}

// ============================================================================
// ERROR HANDLER FOR MUTATIONS
// ============================================================================

export function handleMutationError(error: unknown): string {
  if (error instanceof DirectApiError) {
    if (error.status === 400) {
      return error.message || 'Invalid request';
    }
    if (error.status === 401) {
      return 'Authentication required. Please log in again.';
    }
    if (error.status === 403) {
      return 'You do not have permission to perform this action';
    }
    if (error.status === 404) {
      return 'Resource not found';
    }
    if (error.status === 500) {
      if (
        error.message?.includes('TypeORM') ||
        error.message?.includes('tablePath')
      ) {
        return 'Unable to complete this operation at this time. This is a known issue being resolved.';
      }
      return 'Server error. Please try again later.';
    }

    return error.message || 'An error occurred';
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
}
